import { useCallback, useEffect, useState } from "react";

const CACHE_KEY = "wofy_geo_v2";

const readCache = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    if (process.env.NODE_ENV === "development") console.warn("[useGeo] readCache failed:", e);
    return null;
  }
};

const writeCache = (v) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(v));
  } catch (e) {
    // Private-browsing or storage-quota — non-fatal.
    if (process.env.NODE_ENV === "development") console.warn("[useGeo] writeCache failed:", e);
  }
};

// IP-based fallback (ipwho.is — free, HTTPS, CORS-enabled, no API key).
const ipLookup = async () => {
  const r = await fetch("https://ipwho.is/");
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const d = await r.json();
  if (d && d.success === false) throw new Error(d.message || "geo lookup failed");
  return {
    source: "ip",
    country_code: d.country_code || "",
    country_name: d.country || "",
    country_calling_code: d.calling_code ? `+${String(d.calling_code).replace(/^\+/, "")}` : "",
    city: d.city || "",
    region: d.region || "",
    lat: typeof d.latitude === "number" ? d.latitude : null,
    lng: typeof d.longitude === "number" ? d.longitude : null,
  };
};

// Reverse-geocode a lat/lng to city/country via OpenStreetMap Nominatim (free, no key).
const reverseGeocode = async (lat, lng) => {
  const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=10&addressdetails=1`;
  const r = await fetch(url, {
    // Nominatim requires an identifying UA/Referer; browser fetch sends Referer automatically.
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const d = await r.json();
  const a = d.address || {};
  return {
    source: "gps",
    country_code: (a.country_code || "").toUpperCase(),
    country_name: a.country || "",
    country_calling_code: "",
    city: a.city || a.town || a.village || a.hamlet || a.county || "",
    region: a.state || a.region || "",
    lat,
    lng,
  };
};

// Try the browser Geolocation API. Rejects if the user denies or times out.
const browserGeo = () =>
  new Promise((resolve, reject) => {
    if (!("geolocation" in navigator)) return reject(new Error("no geolocation"));
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => reject(err),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 5 * 60 * 1000 },
    );
  });

export const useGeo = () => {
  const [geo, setGeo] = useState(() => readCache());
  const [loading, setLoading] = useState(!geo);
  const [permission, setPermission] = useState("unknown"); // unknown | granted | denied | prompt

  const runIpFallback = useCallback(async () => {
    try {
      const ipData = await ipLookup();
      setGeo(ipData);
      writeCache(ipData);
    } catch (e) {
      if (process.env.NODE_ENV === "development") console.warn("[useGeo] IP fallback failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const requestPrecise = useCallback(async () => {
    setLoading(true);
    try {
      const { lat, lng } = await browserGeo();
      try {
        const precise = await reverseGeocode(lat, lng);
        setPermission("granted");
        setGeo(precise);
        writeCache(precise);
      } catch {
        // reverse-geocode failed, but we still have coords — keep them
        const bare = { source: "gps", country_code: "", country_name: "", city: "", region: "", country_calling_code: "", lat, lng };
        setPermission("granted");
        setGeo(bare);
        writeCache(bare);
      }
    } catch (err) {
      // User denied or timed out — fall back to IP-based lookup.
      setPermission(err && err.code === 1 ? "denied" : "prompt");
      await runIpFallback();
    } finally {
      setLoading(false);
    }
  }, [runIpFallback]);

  useEffect(() => {
    if (geo) {
      // Already cached — no fetch needed, but check current permission state for the UI.
      if (navigator.permissions) {
        navigator.permissions
          .query({ name: "geolocation" })
          .then((p) => setPermission(p.state))
          .catch(() => {});
      }
      return;
    }
    // Cold start: try to check permission; if already granted, use precise; else use IP.
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((p) => {
          setPermission(p.state);
          if (p.state === "granted") return requestPrecise();
          return runIpFallback();
        })
        .catch(() => runIpFallback());
    } else {
      runIpFallback();
    }
  }, [geo, requestPrecise, runIpFallback]);

  return { geo, loading, permission, requestPrecise, runIpFallback };
};
